import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Link } from 'react-router-dom';

import type { PumsemTree, PumsemTreeNode } from '../model/pumsem.type';
import { pumsemItemPath } from '../lib/pumsemNavigation';
import { pumsemAncestorCodes } from '../lib/pumsemTree';
import { PumsemChangeBadge } from './PumsemChangeBadge';

/**
 * 부문 → 장 → 절 → 항 훑기.
 *
 * ── 왜 필요한가 ─────────────────────────────────────────────
 * 검색만 두면 **무엇이 들어 있는지 모르는 사람은 아무것도 못 본다.** 판을
 * 새로 등록하고 `/pumsem` 을 열었을 때 빈 화면이 나오던 게 그 증상이었다.
 * 실무자는 특정 품으로 점프하러 오지만(그래서 검색이 헤더의 주인공이다),
 * 처음 오는 사람과 "이 판에 뭐가 들어왔나"를 보려는 사람에게는 훑기가 있어야
 * 한다. 둘은 서로를 대체하지 못한다.
 *
 * ── 두 자리에 같은 트리를 쓴다 ──────────────────────────────
 * `page`  — `/pumsem` 첫 화면의 본문 훑기.
 * `sidebar` — 상세를 열고 있는 동안 **왼쪽에 상주하는 목차**.
 * 예전에는 후자가 없어서, 항목 하나를 보고 옆 항목으로 가려면 매번 `/pumsem`
 * 으로 되돌아가 부문·장·절을 처음부터 다시 펼쳐야 했다. 트리를 재구현하지
 * 않고 밀도만 가르는 이유는, 펼침 상태를 두 화면이 **같은 저장소**로 공유해야
 * 목록에서 열고 상세에서 돌아왔을 때 자리가 유지되기 때문이다.
 *
 * 장·절은 **접힌 채로** 시작한다. 부문 하나에 항목이 수천 개라 전부 펼쳐 그리면
 * 첫 렌더에서 화면이 죽는다(관리 트리와 같은 이유). 다만 지금 열려 있는 항목의
 * 조상만은 자동으로 펼친다 — 목차에 자기 자리가 안 보이면 목차가 아니다.
 *
 * 펼친 자리는 `sessionStorage` 에 남긴다. URL 이 아니라 세션에 두는 이유는 이게
 * 남에게 보낼 상태가 아니라 **내 스크롤 위치 같은 것**이기 때문이다.
 */

const OPEN_KEY = 'pumsem.tree.open';

/** 스토리지는 시크릿 창·차단 설정에서 접근 자체가 던진다. 늘 감싼다. */
function readOpen(scope: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(`${OPEN_KEY}.${scope}`);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeOpen(scope: string, open: Set<string>): void {
  try {
    sessionStorage.setItem(`${OPEN_KEY}.${scope}`, JSON.stringify([...open]));
  } catch {
    // 저장이 안 돼도 이번 세션 안에서는 state 로 동작한다.
  }
}

type PumsemTreeVariant = 'page' | 'sidebar';

/** 밀도만 다르다 — 구조·동작은 두 자리가 같아야 한다. */
const VARIANT: Record<
  PumsemTreeVariant,
  {
    root: string;
    heading: string;
    list: string;
    row: string;
    code: string;
    title: string;
    basePad: number;
    indent: number;
  }
> = {
  page: {
    root: 'space-y-7',
    heading:
      'mb-2 flex items-center gap-2 text-[16px] font-bold text-neutral-900 dark:text-neutral-100',
    list: 'overflow-hidden rounded-xl border border-neutral-200 dark:border-white/12',
    row: 'px-3 py-2',
    code: 'w-20 shrink-0 text-[13px] tabular-nums',
    title: 'text-[14.5px]',
    basePad: 12,
    indent: 16,
  },
  sidebar: {
    root: 'space-y-4 pb-6',
    heading:
      'mb-1 flex items-center gap-2 px-4 text-[13px] font-bold tracking-wide text-neutral-400',
    list: 'border-t border-neutral-100 dark:border-white/8',
    row: 'px-4 py-1.5',
    code: 'w-16 shrink-0 text-[12.5px] tabular-nums',
    title: 'text-[13.5px]',
    basePad: 16,
    indent: 12,
  },
};

interface PumsemTreeBrowserProps {
  tree: PumsemTree;
  /** URL 에 판이 고정돼 있으면 항목 링크에도 그대로 물린다. */
  edition: string | null;
  variant?: PumsemTreeVariant;
  /** 지금 열려 있는 부문·코드. 조상을 자동으로 펼치고 그 줄을 표시한다. */
  activeDivision?: string;
  activeCode?: string;
  /** 폰에서 드로어로 쓸 때 항목을 고르면 닫는다. */
  onNavigate?: () => void;
}

export function PumsemTreeBrowser({
  tree,
  edition,
  variant = 'page',
  activeDivision,
  activeCode,
  onNavigate,
}: PumsemTreeBrowserProps) {
  const scope = tree.edition.slug;
  const [open, setOpen] = useState(() => readOpen(scope));
  const style = VARIANT[variant];

  const toggle = useCallback(
    (key: string) => {
      setOpen((prev) => {
        const next = new Set(prev);
        if (!next.delete(key)) next.add(key);
        writeOpen(scope, next);
        return next;
      });
    },
    [scope],
  );

  /**
   * 지금 보고 있는 코드의 조상 마디들. 코드가 접두사 중첩이라 트리를 뒤지지
   * 않고 문자열에서 바로 나온다.
   */
  const activeTrail = useMemo(() => {
    if (!activeDivision || !activeCode) return [] as string[];
    return pumsemAncestorCodes(activeCode).map(
      (code) => `${activeDivision}/${code}`,
    );
  }, [activeDivision, activeCode]);

  /**
   * 조상 자동 펼침은 **렌더 중 state 보정**으로 한다(React 의 "props 가 바뀌면
   * state 를 고친다" 패턴). effect 로 하면 한 번 그린 뒤 다시 그리게 되고,
   * 그 사이 프레임에서 목차가 접힌 채 보인다.
   *
   * 여기서는 `writeOpen` 을 안 부른다 — 자동으로 편 자리는 URL 에서 다시
   * 계산되므로 저장할 이유가 없고, 렌더 중 부수효과는 애초에 두면 안 된다.
   * 사람이 직접 접으면 `toggle` 이 그 판단을 저장한다.
   */
  const trailKey = activeTrail.join('|');
  const [appliedTrail, setAppliedTrail] = useState<string | null>(null);
  if (trailKey !== appliedTrail) {
    setAppliedTrail(trailKey);
    if (activeTrail.length > 0) {
      setOpen((prev) => {
        if (activeTrail.every((key) => prev.has(key))) return prev;
        return new Set([...prev, ...activeTrail]);
      });
    }
  }

  /**
   * 활성 줄로의 스크롤은 **선택이 바뀔 때 한 번**이다.
   *
   * ⚠️ 예전에는 `ref={active ? scrollActiveIntoView : undefined}` 로 콜백 ref 에
   *    붙여 놨는데, 이게 **토글할 때마다 다시 불린다**(재현 확인). 그래서 다른
   *    장을 찾으려고 목차를 내려서 마디를 펼치면 그 순간 보고 있던 항목으로
   *    스크롤이 되돌아왔다 — 목차를 훑는 동작 자체가 불가능했다.
   *    ref 는 위치만 들고, 스크롤은 effect 가 판단한다.
   */
  const activeRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    scrollActiveIntoView(activeRef.current);
  }, [activeDivision, activeCode]);

  const filled = tree.divisions.filter((division) => division.nodes.length > 0);

  if (filled.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-neutral-200 px-4 py-10 text-center text-[14.5px] text-neutral-400 dark:border-white/12">
        {tree.edition.label} 에 등록된 내용이 아직 없습니다.
      </p>
    );
  }

  return (
    <div className={style.root}>
      {filled.map((division) => (
        <section key={division.slug}>
          <h2 className={style.heading}>
            {variant === 'sidebar' ? division.shortTitle : division.title}
            <span className="text-[13px] font-medium text-neutral-400">
              {division.nodes.length}장
            </span>
          </h2>
          <ul className={style.list}>
            {division.nodes.map((node) => (
              <TreeBranch
                key={node.code}
                node={node}
                divisionSlug={division.slug}
                edition={edition}
                depth={0}
                open={open}
                onToggle={toggle}
                style={style}
                activeDivision={activeDivision}
                activeCode={activeCode}
                activeRef={activeRef}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function TreeBranch({
  node,
  divisionSlug,
  edition,
  depth,
  open,
  onToggle,
  style,
  activeDivision,
  activeCode,
  activeRef,
  onNavigate,
}: {
  node: PumsemTreeNode;
  divisionSlug: string;
  edition: string | null;
  depth: number;
  open: Set<string>;
  onToggle: (key: string) => void;
  style: (typeof VARIANT)[PumsemTreeVariant];
  activeDivision?: string;
  activeCode?: string;
  /** 활성 줄의 DOM 위치만 담는다. 스크롤은 부모의 effect 가 결정한다. */
  activeRef?: RefObject<HTMLAnchorElement | null>;
  onNavigate?: () => void;
}) {
  const key = `${divisionSlug}/${node.code}`;
  const expanded = open.has(key);
  const active = divisionSlug === activeDivision && node.code === activeCode;

  // Tailwind 가 동적 클래스를 못 스캔해 들여쓰기는 인라인 style 로 준다.
  const padding = { paddingLeft: style.basePad + depth * style.indent };

  if (node.isItem) {
    return (
      <li className="border-t border-neutral-100 first:border-t-0 dark:border-white/8">
        <Link
          to={pumsemItemPath(divisionSlug, node.code, edition)}
          // 목차에서 지금 자리를 못 찾으면 목차가 아니다. 스크린리더에도
          // 같은 사실을 알린다.
          aria-current={active ? 'page' : undefined}
          ref={active ? activeRef : undefined}
          onClick={onNavigate}
          className={`flex items-center gap-2.5 ${style.row} transition-colors ${
            active
              ? 'bg-brand-50 font-bold text-brand-800 dark:bg-brand-500/10 dark:text-brand-200'
              : 'hover:bg-neutral-50 dark:hover:bg-white/5'
          }`}
          style={padding}
        >
          <code
            className={`${style.code} ${active ? 'text-brand-700 dark:text-brand-300' : 'text-neutral-400'}`}
          >
            {node.code}
          </code>
          <span
            className={`min-w-0 ${style.title} ${active ? '' : 'text-neutral-800 dark:text-neutral-100'}`}
          >
            {node.title}
          </span>
          {node.changeKind && <PumsemChangeBadge change={node.changeKind} />}
        </Link>
      </li>
    );
  }

  // 자식이 없는 마디는 **버튼이 아니다.** 예전엔 토글과 `▸` 가 그려지고
  // `aria-expanded="true"` 까지 갔다가 아무것도 안 열렸다 — 스크린리더에는
  // "확장됨" 이라고 알리고 실제 콘텐츠는 없는 상태였다.
  if (node.children.length === 0) {
    return (
      <li className="border-t border-neutral-100 first:border-t-0 dark:border-white/8">
        <div
          className={`flex items-center gap-2.5 ${style.row} text-neutral-400`}
          style={padding}
        >
          <span aria-hidden className="w-3 shrink-0" />
          <code className={style.code}>{node.code}</code>
          <span className={`min-w-0 ${style.title} font-bold`}>
            {node.title}
          </span>
          <span className="ml-auto shrink-0 text-[13px]">비어 있음</span>
        </div>
      </li>
    );
  }

  return (
    <li className="border-t border-neutral-100 first:border-t-0 dark:border-white/8">
      {/*
        장·절 줄은 **통째로 토글**이다. 이 줄에 목차 페이지 링크를 겸하게 하면
        펼치려고 누른 사람이 매번 화면을 갈아탄다 — 트리의 존재 이유가 제자리
        훑기인데 그게 깨진다. 장·절 주소(`/pumsem/machine/6-2`)는 상세 화면의
        빵부스러기와 URL 직접 입력으로 들어간다.
      */}
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => onToggle(key)}
        className={`flex w-full items-center gap-2.5 ${style.row} text-left transition-colors ${
          active
            ? 'bg-brand-50 dark:bg-brand-500/10'
            : 'hover:bg-neutral-50 dark:hover:bg-white/5'
        }`}
        style={padding}
      >
        <span aria-hidden className="w-3 shrink-0 text-[12px] text-neutral-400">
          {expanded ? '▾' : '▸'}
        </span>
        <code className={`${style.code} text-neutral-400`}>{node.code}</code>
        <span
          className={`min-w-0 ${style.title} font-bold text-neutral-800 dark:text-neutral-100`}
        >
          {node.title}
        </span>
        <span className="ml-auto shrink-0 text-[13px] text-neutral-400">
          {node.children.length}
        </span>
      </button>

      {expanded && (
        <ul>
          {node.children.map((child) => (
            <TreeBranch
              key={child.code}
              node={child}
              divisionSlug={divisionSlug}
              edition={edition}
              depth={depth + 1}
              open={open}
              onToggle={onToggle}
              style={style}
              activeDivision={activeDivision}
              activeCode={activeCode}
              activeRef={activeRef}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/** 현재 줄을 컨테이너의 위아래 **가운데**에 놓기 위한 나눗수. */
const CENTER_DIVISOR = 2;

/** 위·아래로 실제 스크롤되는 가장 가까운 조상. 없으면 null. */
function scrollBoxOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    const scrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (scrollable && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

/**
 * 현재 줄을 목차 안에서 보이게 한다. 조상을 펼쳐 놔도 접힌 영역 아래에 있으면
 * 안 보이기 때문이다. **부르는 쪽이 시점을 고른다** — 선택이 바뀔 때만이다.
 *
 * ⚠️ `scrollIntoView` 를 쓰지 않는다. 그건 **문서까지 포함해 조상 스크롤을 전부**
 *    움직이므로, sticky 사이드바 안에서 부르면 읽고 있던 본문이 같이 밀린다.
 *    여기서는 컨테이너의 `scrollTop` 만 건드린다 — 창은 절대 안 움직인다.
 */
function scrollActiveIntoView(element: HTMLAnchorElement | null): void {
  if (!element) return;

  const box = scrollBoxOf(element);
  if (!box) return;

  const boxRect = box.getBoundingClientRect();
  const rect = element.getBoundingClientRect();
  // 이미 다 보이면 아무것도 안 한다. 안 그러면 클릭할 때마다 목차가 들썩인다.
  if (rect.top >= boxRect.top && rect.bottom <= boxRect.bottom) return;

  // 가운데로. 대입값은 브라우저가 알아서 [0, scrollHeight] 로 자른다.
  box.scrollTop +=
    rect.top - boxRect.top - (boxRect.height - rect.height) / CENTER_DIVISOR;
}
